// src/pages/CourseTime/components/slides/assessment/ChoiceQuestion.comp.jsx
import React from 'react';
import styled from 'styled-components';

// Styled components
const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const OptionCard = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px 24px;
  border-radius: 12px;
  border: 1.5px solid ${props => props.selected ? (props.borderColor || '#fe696e') : '#e3e5e8'};
  background-color: ${props => props.selected ? `${props.borderColor}14` : '#fff'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.borderColor || '#fe696e'};
  }
`;

const OptionBadge = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid ${props => props.selected ? props.borderColor : '#c7cbd1'};
  background-color: ${props => props.selected ? props.borderColor : 'transparent'};
  color: ${props => props.selected ? '#fff' : '#9aa0a6'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
`;

const OptionCardText = styled.div`
  color: #2b2f36;
  font-weight: 600;
  font-size: 16px;
  text-align: left;
`;

const CustomInputField = styled.input`
  width: 100%;
  max-width: 600px;
  padding: 14px 20px;
  border: 2px solid ${props => props.borderColor || '#fe696e'};
  border-radius: 8px;
  font-size: 16px;
  font-family: "Source Sans Pro", "Helvetica Neue", sans-serif;
  background-color: ${props => props.selected ? props.backgroundColor : '#fff'};
  color: ${props => props.selected ? '#fff' : '#333'};

  &:focus {
    outline: none;
    border-color: ${props => props.focusColor || '#e05560'};
    box-shadow: 0 0 0 3px rgba(254, 105, 110, 0.1);
  }

  &::placeholder {
    color: ${props => props.selected ? 'rgba(255,255,255,0.7)' : '#999'};
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// Default option list for single-answer (MCQ) and multi-answer (MAQ) questions:
// full-width lettered option cards, with an inline text field for any option the
// ui_rules flagged as INDEX_EFFECT/USER_INPUT.
const ChoiceQuestion = ({
  processedChoices,
  inputQuestions,
  unselectQuestions,
  uiRules,
  isMultiSelect,
  slideId,
  currentAnswer,
  setAnswersMap,
  themeColor,
  renderSpokenText
}) => {
  // Handle option selection with QuestionPoster logic
  const handleOptionSelect = (index) => {
    if (isMultiSelect) {
      // Multiple answer question - toggle selection
      setAnswersMap(prev => {
        // The stored answer may be a leftover non-array (free-text string,
        // custom-input object, MCQ index) from a prior interaction restore.
        const stored = prev[slideId];
        const currentAnswers = Array.isArray(stored) ? stored : [];
        let newAnswers = [...currentAnswers];

        if (currentAnswers.includes(index)) {
          // Deselect
          newAnswers = currentAnswers.filter(i => i !== index);
        } else {
          // Select
          // If UI rules exist, handle unselect logic
          if (uiRules && uiRules.length > 0) {
            newAnswers = currentAnswers.filter(val => !unselectQuestions.includes(val));
            if (unselectQuestions.includes(index)) {
              newAnswers = [];
            }
          } else {
            // Handle "None of the above" / "All of the above" logic
            currentAnswers.forEach(choiceIdx => {
              const choice = processedChoices[choiceIdx];
              if (choice && (
                choice.trim().toLowerCase().includes('none of the above') ||
                choice.trim().toLowerCase().includes('all of the above') ||
                choice.trim().toLowerCase().includes('해당하는 선택지 없음.')
              )) {
                newAnswers = newAnswers.filter(i => i !== choiceIdx);
              }
            });

            const selectedChoice = processedChoices[index];
            if (selectedChoice && (
              selectedChoice.trim().toLowerCase().includes('none of the above') ||
              selectedChoice.trim().toLowerCase().includes('all of the above') ||
              selectedChoice.trim().toLowerCase().includes('해당하는 선택지 없음.')
            )) {
              newAnswers = [];
            }
          }

          newAnswers.push(index);
        }

        return {
          ...prev,
          [slideId]: newAnswers
        };
      });
    } else {
      // Single answer question - toggle on/off
      setAnswersMap(prev => {
        const currentSelected = prev[slideId];
        return {
          ...prev,
          [slideId]: currentSelected === index ? null : index
        };
      });
    }
  };

  const handleCustomInputChange = (index, text) => {
    setAnswersMap(prev => {
      const newAnswers = {
        ...prev,
        [slideId]: {
          ...(typeof prev[slideId] === 'object' ? prev[slideId] : {}),
          customInput: text,
          selectedIndex: text ? index : null
        }
      };
      return newAnswers;
    });
  };

  const isOptionSelected = (index) => {
    if (!currentAnswer && currentAnswer !== 0) return false;

    if (isMultiSelect) {
      return Array.isArray(currentAnswer) && currentAnswer.includes(index);
    }

    // Handle custom input case
    if (typeof currentAnswer === 'object' && currentAnswer.selectedIndex !== undefined) {
      return currentAnswer.selectedIndex === index;
    }

    return currentAnswer === index;
  };

  const getCustomInputValue = () => {
    if (typeof currentAnswer === 'object' && currentAnswer.customInput) {
      return currentAnswer.customInput;
    }
    return '';
  };

  return (
    <>
      <OptionsList>
        {processedChoices.map((option, idx) => {
          const letter = String.fromCharCode(65 + idx);
          const selected = isOptionSelected(idx);

          // Check if this option should be a custom input field
          if (inputQuestions.includes(idx)) {
            return (
              <OptionCard key={idx} selected={selected} borderColor={themeColor}>
                <OptionBadge selected={selected} borderColor={themeColor}>{letter}</OptionBadge>
                <CustomInputField
                  type="text"
                  placeholder={option}
                  value={getCustomInputValue()}
                  onChange={(e) => handleCustomInputChange(idx, e.target.value)}
                  selected={false}
                  borderColor={themeColor}
                  backgroundColor={themeColor}
                  focusColor={themeColor}
                  style={{ border: 'none', padding: 0, maxWidth: 'none' }}
                />
              </OptionCard>
            );
          }

          return (
            <OptionCard
              key={idx}
              selected={selected}
              borderColor={themeColor}
              onClick={() => handleOptionSelect(idx)}
            >
              <OptionBadge selected={selected} borderColor={themeColor}>{letter}</OptionBadge>
              <OptionCardText>
                <span>{renderSpokenText ? renderSpokenText(`option_${idx}`, option) : option}</span>
              </OptionCardText>
            </OptionCard>
          );
        })}
      </OptionsList>
    </>
  );
};

export default ChoiceQuestion;
